import { useRef, useState } from "react";
import { getClassName } from "@pairtreefamily/utils";
import React from "react";
import { ProductsComparisonTableHeader } from "./productsComparisonTableHeader";
import { ProductsComparisonTableBody } from "./productsComparisonTableBody";
import { ProductsComparisonTableFoot } from "./productsComparisonTableFoot";
import { ProductsComparisonMobileCard } from "./productsComparisonMobileCard";
import { Colors } from "../../types/colors";
import { ProductPricingResponse } from "../../types/coreApiMirror";
import * as Schema from "../../types/schema";
import { PricingLandingData } from "../../queries/pricingLandingQuery";
import { State } from "../../types/state";

export type ProductsComparisonProps = {
  moduleInfo: Pick<PricingLandingData, "moduleProductsComparison">;
  states?: State[];
  selectedState?: State;
  updateState?: (s: State | undefined) => void;
  stateSelectorImage?: Schema.CustomImage;
  prices: ProductPricingResponse[];
};

const colorMap = {
  darkblue: "text-darkblue",
  teal: "text-teal",
  green: "text-green",
  lightgreen: "text-lightgreen",
  peach: "text-peach",
  rust: "text-rust",
};

type PricingColor = Extract<
  Colors.Solid,
  "darkblue" | "teal" | "green" | "lightgreen" | "peach" | "rust"
>;

const getMostExpensiveProduct = (
  items: ProductPricingResponse[],
): ProductPricingResponse => {
  return items.sort(({ unit_amount: a }, { unit_amount: b }) => b - a)[0];
};

function getProductPrice(
  currentPrices: ProductPricingResponse[],
  coreProductCode: string,
): ProductPricingResponse[] {
  return currentPrices?.filter(
    (item) =>
      item.internalId.replace("_monthly", "").replace("_annual", "") ==
      coreProductCode,
  );
}

export function ProductsComparison(props: ProductsComparisonProps) {
  const moduleInfo = props.moduleInfo.moduleProductsComparison.moduleInput;

  const { states, selectedState, stateSelectorImage, prices, updateState } =
    props;

  const [showHeader, setShowHeader] = useState<boolean>(false);

  const stickyHeader = useRef<HTMLTableElement>(null);

  return (
    <>
      <div id="featureComparison">
        <div
          className={getClassName(
            "sticky top-[74px] z-10  bg-white",
            !showHeader && "opacity-0",
          )}
        >
          <table
            ref={stickyHeader}
            className="hidden w-full table-fixed pt-4 md:block"
          >
            <thead className="table w-full">
              <tr className="w-full">
                <th
                  style={{
                    width: `calc(100% / (${
                      (moduleInfo.products?.length ?? 0) + 1
                    })`,
                  }}
                >
                  FEATURE{" "}
                </th>
                {moduleInfo.products?.map((product) => (
                  <td
                    key={product.product?._id}
                    className={getClassName(
                      "text-overline-2 px-4 text-center",
                      colorMap[product.product?.color as PricingColor],
                    )}
                    style={{
                      width: `calc(100% / (${
                        (moduleInfo.products?.length ?? 0) + 2
                      })`,
                    }}
                  >
                    Pairtree {product.product?.name}
                  </td>
                ))}
              </tr>
            </thead>
          </table>
          <div className="hidden h-1 w-full shadow-[0px_4px_4px_rgba(0,0,0,0.25)] md:block"></div>
        </div>

        <table className="mt-8 hidden w-full table-fixed md:block">
          <ProductsComparisonTableHeader
            moduleInfo={props.moduleInfo}
            selectedState={selectedState}
            prices={prices}
            stickyHeader={stickyHeader}
            updateShowHeader={(value) => setShowHeader(value)}
          />

          <ProductsComparisonTableBody moduleInfo={props.moduleInfo} />

          <ProductsComparisonTableFoot moduleInfo={props.moduleInfo} />
        </table>
        <div className="block md:hidden">
          {moduleInfo.products.map((product) => {
            const productPrice = getProductPrice(
              prices,
              product.product?.coreProductCode ?? "",
            );

            const monthPrice = getMostExpensiveProduct(
              productPrice?.filter(
                (item) =>
                  item.type === "recurring" &&
                  item.recurring.interval === "month",
              ),
            );
            const yearPrice = getMostExpensiveProduct(
              productPrice?.filter(
                (item) =>
                  item.type === "recurring" &&
                  item.recurring.interval === "year",
              ),
            );
            const oneTimePrice = getMostExpensiveProduct(
              productPrice?.filter((item) => item.type === "one_time"),
            );

            return (
              <ProductsComparisonMobileCard
                key={product.product?._id}
                monthPrice={monthPrice}
                yearPrice={yearPrice}
                oneTimePrice={oneTimePrice}
                moduleInfo={props.moduleInfo}
                product={product.product}
                productPriceLength={productPrice.length}
                selectedState={selectedState}
                isBestDeal={
                  (getProductPrice(prices, "complete").length > 0 &&
                    product.product?.coreProductCode === "complete") ||
                  (getProductPrice(prices, "complete").length === 0 &&
                    product.product?.coreProductCode === "connect_plus")
                }
              />
            );
          })}
        </div>
      </div>
    </>
  );
}
